import * as React from 'react';
import { Box } from '@apia/theme';
import { useMountEffect, useUnmountEffect } from '@react-hookz/web/esm';
import { UseFormReturn } from 'react-hook-form';
import useStateRef from 'react-usestateref';
import { shallowEqual } from 'react-redux';
import { Modal, ProgressBar } from '../../components/common';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import useModal from '../../hooks/useModal';
import store from '../../store';
import { apiaApiActions } from '../../store/apiaApiSlice';
import {
  TModify,
  TApiaAction,
  TApiaFunction,
  TApiaLoad,
  TApiaLoadForm,
  TApiaLoadText,
  TApiaFormElement,
} from '../../types';
import { arrayOrArray } from '../../utils/util';
import EventEmitter from '../../utils/util/eventEmitter';
import {
  ApiaActions,
  IApiaApiRequestConfig,
  TApiaApiAxiosResponse,
} from './apiaApi';
import WithEventsValue from '../../utils/WithEventsValue';
import { enforceFocus, focusSelector } from '../../utils/dom';
import { notify } from '../../components/common/notifications/util';
import ApiaApiFieldsContainer from './fields/ApiaApiFieldsContainer';
import Form from '../../components/common/validation/Form';
import ApiaApiContext from './ApiaApiContext';
import validationsStore from '../../store/validation/validationStore';
import ApiaApiButtonsContainer from './buttons/ApiaApiButtonsContainer';
import { TModal } from '@apia/components';
import { TDispatchedNotification } from '@apia/notifications';

type TMessageLocal = {
  title: string;
  predicate: string;
  type?: TDispatchedNotification['type'];
};
type TMethod = {
  name: string;
  props: {
    attributes?: Record<string, unknown>;
    currentUrl?: string;
    funCall?: string;
    inlineArguments?: string[];
    messages?: unknown;
  };
};
const ApiaApiMessenger = new (class extends EventEmitter<{
  message: TMessageLocal;
}> {})();
const FunctionsDispatcher = new (class extends EventEmitter<{
  method: TMethod;
}> {})();

export type TApiaApiMethodHandler = {
  close: () => void;
  configuration?: THandleConfiguration;
  formDefinition: TApiaLoad<TApiaLoadForm>;
  setModalProps?: (
    configuration: THandleConfiguration,
    formDefinition?: TApiaLoad<TApiaLoadForm>,
  ) => TModal | null;
  reset: () => void;
  setError: typeof notify;
  setMessage: (message: Record<string, unknown>) => unknown;
  setState: React.Dispatch<React.SetStateAction<IApiaApiHandlerState>>;
  state: IApiaApiHandlerState;
  setValue: (name: string, value: string) => void;
};

export type TApiaApiMethod = (
  handler: TApiaApiMethodHandler,
  props: TMethod['props'],
) => Promise<void> | void;

export type TApiaApiField = TApiaApiMethodHandler & {
  element: TModify<TApiaFormElement, { onChange: React.ChangeEventHandler }>;
};

export const apiaApiForm = 'ApiaApiForm';

type TStoredApiaApiMethod = (props?: TMethod['props']) => unknown;

const methods: Record<string, TStoredApiaApiMethod> = {};

export async function getFunction(
  name: string,
  handler: TApiaApiMethodHandler,
): Promise<TStoredApiaApiMethod | undefined> {
  return new Promise((resolve) => {
    try {
      const separateNameFromParametersRegex =
        /^\/?([\w\d_-]+)\/?(?:\(([^)]*)\))?$/;
      const match = name.match(separateNameFromParametersRegex);
      if (match) {
        const functionName = match[1];
        const parameters = match[2];
        const path: string = [
          ...(handler.configuration?.methodsPath
            ?.split('/')
            .filter((current) => !!current) ?? []),
          functionName,
        ].join('/');
        const storeMethodAndResolve = (result: { default: TApiaApiMethod }) => {
          if (typeof result.default !== 'function') notFound();
          const method = result.default;
          methods[functionName] = (props?: TMethod['props']) =>
            method(handler, {
              ...props,
              inlineArguments:
                parameters?.split(',').map((argument) => {
                  if (argument.startsWith("'") || argument.startsWith('"'))
                    return argument.slice(1, argument.length - 1);
                  return argument;
                }) ?? [],
            });
          resolve(methods[functionName]);
        };

        const notFound = () => {
          throw new Error(
            `${functionName}.ts not found at ${path}.ts nor ./${functionName}.ts`,
          );
        };

        import(
          /* webpackChunkName: "api-methods-[request]" */ `./methods/${path}.ts`
        )
          .then(storeMethodAndResolve)
          .catch(() => {
            if (handler.configuration?.methodsPath !== undefined)
              import(
                /* webpackChunkName: "api-methods-[request]" */ `./methods/${functionName}.ts`
              )

                .then(storeMethodAndResolve)
                .catch(notFound);
            else notFound();
          });
      }
    } catch (e) {
      console.error(e);
      handler.reset();
      handler.setError({
        type: 'danger',
        message: 'Error while loading current method.',
      });
    }
  });
}

export function isForm(
  responseObject: Record<string, unknown>,
): responseObject is TApiaLoad<TApiaLoadForm> {
  return (
    typeof responseObject.canClose === 'boolean' &&
    typeof responseObject.type === 'string' &&
    !!responseObject.form
  );
}

export function isFunction(
  responseObject: Record<string, unknown>,
): responseObject is TApiaLoad<TApiaFunction> {
  return (
    typeof responseObject.canClose === 'boolean' &&
    typeof responseObject.type === 'string' &&
    !!responseObject.function
  );
}

function isMessage(
  responseObject: Record<string, unknown>,
): responseObject is TApiaLoad<TApiaLoadText> {
  if (
    typeof responseObject.canClose === 'boolean' &&
    typeof responseObject.type === 'string' &&
    typeof responseObject.text === 'object' &&
    responseObject.text &&
    typeof (responseObject.text as Record<string, unknown>).label === 'string'
  ) {
    return true;
  }
  return false;
}

export interface IModalConfig {
  modalTitle?: string;
  onAction?: (action: TApiaAction) => unknown;
  onClose?: () => void;
  onMessage?: (message: Record<string, unknown>) => unknown;
  onMessageClose?: () => unknown;
  onSubmitted?: (
    handler: TApiaApiMethodHandler,
    response: TApiaApiAxiosResponse<Record<string, unknown>> | null,
  ) => unknown;
}

const modalConfigurationHandler = new WithEventsValue<THandleConfiguration>();

function useModalConfiguration() {
  const [, setValue, value] = useStateRef<THandleConfiguration | undefined>(
    undefined,
  );

  const handleChange = React.useCallback(
    (newEventsHandler: THandleConfiguration | undefined) => {
      setValue(newEventsHandler);
    },
    [setValue],
  );

  useMountEffect(() => {
    modalConfigurationHandler.on('update', handleChange);
  });

  useUnmountEffect(() => modalConfigurationHandler.off('update', handleChange));

  return value;
}

export type THandleConfiguration = Partial<
  Pick<
    IApiaApiRequestConfig<unknown>,
    'modalConfiguration' | 'methodsPath' | 'setModalProps'
  >
>;

export function handle(
  responseObject: Record<string, unknown>,
  currentUrl: string,
  configuration: THandleConfiguration,
) {
  modalConfigurationHandler.value = configuration;
  if (isForm(responseObject)) {
    store.dispatch(
      apiaApiActions.setCurrentForm({
        currentForm: responseObject,
      }),
    );
    return true;
  }
  if (isFunction(responseObject)) {
    FunctionsDispatcher.emit('method', {
      name: responseObject.function.name,
      props: {
        messages: responseObject.function.messages,
        attributes: responseObject.function,
        currentUrl,
      },
    });
    return true;
  }
  if (isMessage(responseObject)) {
    let type: TDispatchedNotification['type'] = 'warning';
    if (responseObject.text.addClass.match(/success/)) {
      type = 'success';
    } else if (responseObject.text.addClass.match(/danger/)) {
      type = 'danger';
    }
    ApiaApiMessenger.emit('message', {
      predicate: responseObject.text.label,
      title: responseObject.text.title,
      type,
    });
    return true;
  }
  return false;
}

export interface IApiaApiHandlerState {
  disabled: boolean;
  isLoading: boolean;
  isMultipart: boolean;
  progress: number;
  errors: UseFormReturn<Record<string, string>>['formState']['errors'];
  windowIndex: number;
}

const initialState: Omit<IApiaApiHandlerState, 'windowIndex'> = {
  disabled: false,
  isLoading: false,
  isMultipart: false,
  progress: 0,
  errors: {},
};

const ApiaApiHandler = () => {
  const configuration = useModalConfiguration();

  const [state, setState] = React.useState<IApiaApiHandlerState>({
    ...initialState,
    windowIndex: -1,
  });

  const currentForm = useAppSelector(
    (globalState) => globalState.apiaApiSlice.currentForm,
    shallowEqual,
  );

  const setValue = React.useCallback(
    (name: string, value: string) =>
      validationsStore.setFieldValue(apiaApiForm, name, value),
    [],
  );

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (!currentForm.definition) return;
    const elements = arrayOrArray(
      currentForm.definition?.form?.elements?.element ?? [],
    );

    let isMultipart = false;
    const elementsValues = Object.values(elements);
    for (let i = 0; i < elementsValues.length; i++) {
      const element = elementsValues[i];
      if (element.type === 'file') {
        isMultipart = true;
        break;
      }
    }
    setState((currentState) => ({
      ...currentState,
      ...initialState,
      isMultipart,
    }));
  }, [currentForm.definition]);

  const { show, onClose, ...modalHookProps } = useModal();

  const close = React.useCallback(() => {
    onClose();
    dispatch(apiaApiActions.setCurrentForm({ currentForm: undefined }));
    setState((currentState) => ({ ...currentState, ...initialState }));
    if (configuration.current?.modalConfiguration?.onClose)
      configuration.current.modalConfiguration.onClose();
    store.dispatch(apiaApiActions.resetForm());
  }, [configuration, dispatch, onClose]);

  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const elements = modalRef.current?.querySelectorAll(
      'a,input,textarea,button',
    );
    if (elements?.length === 1)
      void enforceFocus(([...elements] as HTMLElement[])[0]);
  }, [modalRef]);

  const setError = React.useCallback(
    (notification: Parameters<typeof notify>[0]) => {
      notify({ ...notification });
    },
    [],
  );

  const initialFocusGetter = React.useCallback((ref: HTMLElement) => {
    return ref?.querySelector(focusSelector) as HTMLElement;
  }, []);

  const defaultModalProps = React.useMemo(
    (): TModal => ({
      onClose: close,
      id: apiaApiForm,
      title:
        configuration.current?.modalConfiguration?.modalTitle ??
        currentForm.definition?.form.title,
      size: 'md-fixed',
      shouldCloseOnEsc: !state.disabled,
      shouldCloseOnOverlayClick: !state.disabled,
      initialFocusGetter,
    }),
    [
      close,
      configuration,
      currentForm.definition?.form.title,
      initialFocusGetter,
      state.disabled,
    ],
  );

  const getModalProps = React.useCallback(
    (innerHandler: TApiaApiMethodHandler) => {
      if (innerHandler.setModalProps) {
        const overrideProps = innerHandler.setModalProps?.(
          {
            methodsPath: innerHandler.configuration?.methodsPath,
            modalConfiguration: innerHandler.configuration?.modalConfiguration,
          },
          innerHandler.formDefinition,
        );
        if (overrideProps) {
          const currentOnClose = () => {
            overrideProps?.onClose?.();
            defaultModalProps.onClose?.();
          };
          const currentOnExited = () => {
            overrideProps?.onExited?.();
            modalHookProps?.onExited?.();
          };
          return {
            ...defaultModalProps,
            ...overrideProps,
            ...modalHookProps,
            onClose: currentOnClose,
            onExited: currentOnExited,
          };
        }
      }
      return { ...defaultModalProps, ...modalHookProps };
    },
    [defaultModalProps, modalHookProps],
  );

  const getHandler = React.useCallback(
    (): TApiaApiMethodHandler => ({
      close,
      configuration: configuration.current,
      formDefinition: store.getState().apiaApiSlice.currentForm
        .definition as TApiaLoad<TApiaLoadForm>,
      setModalProps: configuration.current?.setModalProps,
      reset: () =>
        setState((currentState) => ({ ...currentState, ...initialState })),
      setError,
      setMessage: (message) => {
        if (configuration.current?.modalConfiguration?.onMessage)
          configuration.current.modalConfiguration.onMessage(message);
      },
      setState,
      state,
      setValue,
    }),
    [close, configuration, setError, setValue, state],
  );

  const handler = getHandler();
  const modalProps = getModalProps(handler);

  const handleAction = React.useCallback(
    async (action: TModify<TApiaAction, { param: string[] }>) => {
      const innerHandler = getHandler();
      if (action.toDo === 'ajaxHiddeAll') {
        close();
      }
      if (action.toDo === 'functionTimedCall') {
        const method = await getFunction(
          arrayOrArray(action.param)[1],
          innerHandler,
        );

        if (method) {
          /*
           * Aquí el timeout se utiliza debido a que Apia lo establece de esa
           * forma,
           * Existe un retorno de Apia llamado functionTimedCall que espera que
           * una función se ejecute luego de un timeout, y yo solamente respeté
           * dicho comportamiento.
           */
          setTimeout(
            () =>
              method({
                currentUrl: innerHandler.formDefinition?.form.action ?? 'noUrl',
              }),
            Number(action.param[0]),
          );
        }
      }
    },
    [close, getHandler],
  );

  const handleFunction = React.useCallback(
    async ({ name, props: { messages, attributes, currentUrl } }: TMethod) => {
      const innerHandler = getHandler();
      const method = await getFunction(`${name}`, innerHandler);
      if (method) {
        method({ messages, attributes, currentUrl });
      }
    },
    [getHandler],
  );

  const handleMessage = React.useCallback(
    (ev: TMessageLocal) => {
      const innerHandler = getHandler();
      notify({
        message: ev.predicate,
        type: ev.type ?? 'warning',
        onClose: innerHandler.configuration?.modalConfiguration?.onMessageClose,
      });
      const onMessage =
        innerHandler.configuration?.modalConfiguration?.onMessage;
      if (onMessage) {
        onMessage(ev);
      }
    },
    [getHandler],
  );

  React.useEffect(() => {
    ApiaApiMessenger.on('message', handleMessage);
    FunctionsDispatcher.on('method', handleFunction);
    ApiaActions.on('action', handleAction);

    return () => {
      ApiaApiMessenger.off('message', handleMessage);
      FunctionsDispatcher.off('method', handleFunction);
      ApiaActions.off('action', handleAction);
    };
  }, [handleAction, handleFunction, handleMessage, state.windowIndex]);

  React.useEffect(() => {
    if (currentForm.definition) {
      show();
    }
  }, [currentForm.definition, show]);

  return (
    <ApiaApiContext id={apiaApiForm}>
      <Modal
        {...modalProps}
        ref={modalRef}
        variant="layout.common.modals.apiaApi"
      >
        {currentForm.definition && (
          <Box>
            <Form
              name={apiaApiForm}
              unregisterOnUnmount
              avoidFieldsOverride
              className="handler__form"
            >
              <ApiaApiFieldsContainer
                definition={currentForm.definition}
                {...handler}
              />
              {state.isMultipart && state.progress > 0 && (
                <Box className="progressBox">
                  <ProgressBar
                    id="ApiaApiHandler progress"
                    progress={state.progress}
                    loading
                  />
                </Box>
              )}
              <ApiaApiButtonsContainer
                definition={currentForm.definition}
                {...handler}
              />
            </Form>
          </Box>
        )}
      </Modal>
    </ApiaApiContext>
  );
};

export default React.memo(ApiaApiHandler);
